import { Flex, Divider, styled, Icon, Box, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { globalAttributes } from '../../../../common/global-attributes';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { MAX_AGENCIES_TO_SHOW } from '../../../configuration';
import {
    generateAgencySubdivisionGroupedByClassType,
    mapMultiAgencyClassificationDtoToDisplay,
} from '../helpers';
import { AgencyDisplay, AgencySubdivisionGroup } from '../types';
import { useBootstrapState } from '../../../../bootstrap/bootstrap-provider';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { CommonSize, getOneSizeSmaller } from '../../../../common/helpers/utils';

const { POLICE, FIRE, EMS } = globalAttributes.agencyTypeGlobal;

const Section = styled('div')<{ showAll: boolean }>`
    > div:last-child {
        margin-bottom: ${({ showAll }) => (showAll ? '8px' : 0)};
    }
`;

const ViewMoreButton = styled('button')<{ size: CommonSize }>`
    font-size: size;
    line-height: 14px;
    text-transform: uppercase;
`;

const AddressIsOOJForAllAgenciesMessage = ({ size }: { size?: CommonSize }) => {
    return (
        <Text fontSize={size} data-testid="MAP_LOCATION_PIN_ALL_AGENCIES_OOJ_MESSAGE">
            Address is OOJ for all configured agencies
        </Text>
    );
};

const AgencySubdivision = ({ agency, size }: { agency: AgencyDisplay; size?: CommonSize }) => {
    return (
        <Flex flexDirection="column" marginTop={2}>
            <Text
                fontSize={size}
                variant="caption"
                data-testid="MAP_LOCATION_PIN_SUBDIVISIONS}_LABEL"
            >{`${agency.agencyName} Subd.`}</Text>
            <Text
                fontSize={size}
                style={{ wordBreak: 'break-word' }}
                data-testid="MAP_LOCATION_PIN_SUBDIVISIONS"
            >
                {agency.subdivisionsLabel}
            </Text>
        </Flex>
    );
};

const GroupAgencySubdivision = ({
    group,
    size,
}: {
    group: AgencySubdivisionGroup;
    size?: CommonSize;
}) => {
    const smallerSize = getOneSizeSmaller(size);

    return (
        <Box marginTop={3}>
            <Text>{group.agencyTypeDisplay}</Text>
            <Flex alignItems="center" marginTop={2} gap={2}>
                <Text fontSize={smallerSize}>D-Area</Text>
                <Text fontSize={size} style={{ wordBreak: 'break-word' }}>
                    {group.dispatchArea}
                </Text>
            </Flex>
            {group.agencies.length === 0 ? (
                <Flex flexDirection="column" marginTop={2}>
                    <Text fontSize={smallerSize} data-testid="MAP_LOCATION_PIN_SUBDIVISIONS}_LABEL">
                        Subd.
                    </Text>
                    <AddressIsOOJForAllAgenciesMessage size={size} />
                </Flex>
            ) : (
                group.agencies.map((agency) =>
                    agency.shouldShow ? (
                        <AgencySubdivision key={agency.agencyId} agency={agency} size={size} />
                    ) : null
                )
            )}
        </Box>
    );
};

export const LocationInfoMultiAgencySection = observer(
    function LocationInfoMultiAgencySection(): React.ReactElement {
        const mapStore = useEsriMapStore();
        const {
            manageDispatchAreasView,
            agencyProfileOptions,
            subdivisionOptionsBootstrapResponse,
        } = useBootstrapState();

        const { isFirstResponder } = useApplicationType();
        const size = isFirstResponder ? 'lg' : 'md';

        const geoPointLocation = mapStore.locationSearchSelectedGeoPointLocation;
        const latitude = geoPointLocation?.latitude;
        const longitude = geoPointLocation?.longitude;
        const multiAgencyClassificationDtos = geoPointLocation?.multiAgencyClassificationDtos;

        const allDispatchAreaObjects = manageDispatchAreasView?.allDispatchAreaObjects;

        const agencyIdToSubdivisionOptionsBootstrapView =
            subdivisionOptionsBootstrapResponse?.agencyIdToSubdivisionOptionsBootstrapView;

        const coordsRef = useRef({ lat: latitude, lng: longitude });
        const { lat, lng } = coordsRef.current;

        const {
            multiAgencySubdivisions,
            dispatchAreaByClass,
            isEverythingOOJ,
        } = mapMultiAgencyClassificationDtoToDisplay(
            multiAgencyClassificationDtos,
            agencyProfileOptions,
            allDispatchAreaObjects,
            agencyIdToSubdivisionOptionsBootstrapView
        );

        const agenciesToShow = multiAgencySubdivisions.filter(
            (agency) => !!agency.subdivisionsLabel
        ).length;

        const [showAll, setShowAll] = useState(agenciesToShow <= MAX_AGENCIES_TO_SHOW);

        useEffect(() => {
            if (lat !== latitude || lng !== longitude) {
                coordsRef.current = { lat: latitude, lng: longitude };
                setShowAll(agenciesToShow <= MAX_AGENCIES_TO_SHOW);
            }
        }, [lat, lng, latitude, longitude, agenciesToShow]);

        const agencySubdivisionsGroups = generateAgencySubdivisionGroupedByClassType(
            multiAgencySubdivisions,
            dispatchAreaByClass,
            agenciesToShow,
            showAll
        );

        const policeGroup = agencySubdivisionsGroups[POLICE];
        const fireGroup = agencySubdivisionsGroups[FIRE];
        const emsGroup = agencySubdivisionsGroups[EMS];

        return (
            <Section showAll={showAll} data-testid="MAP_LOCATION_PIN_INFO_MULTI_AGENCY_SECTION">
                <Flex direction="column" padding="12px 18px">
                    <Box marginBottom={isEverythingOOJ ? 3 : 0}>
                        <Text fontSize={size} fontWeight="bold">
                            Location Info
                        </Text>
                    </Box>
                    {isEverythingOOJ && <AddressIsOOJForAllAgenciesMessage size={size} />}
                    {!isEverythingOOJ && (
                        <>
                            {policeGroup && (
                                <GroupAgencySubdivision group={policeGroup} size={size} />
                            )}
                            {fireGroup && <GroupAgencySubdivision group={fireGroup} size={size} />}
                            {emsGroup && <GroupAgencySubdivision group={emsGroup} size={size} />}
                        </>
                    )}
                    {!showAll && (
                        <Flex
                            data-testid="MAP_LOCATION_PIN_VIEW_MORE_BUTTON"
                            justifyContent="center"
                            padding="10px 0"
                            cursor="pointer"
                            gap={2}
                            onClick={() => setShowAll(true)}
                        >
                            <ViewMoreButton size={size}>View More</ViewMoreButton>
                            <Icon icon="Open" />
                        </Flex>
                    )}
                </Flex>
                <Divider />
            </Section>
        );
    }
);
